import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { devicesService } from '../../services/devices';
import { localStorageService } from '../../services/localStorage';
import type { Climb } from '../../types/climb';
import type { Board, BoardClimbsQuery } from '../../types/device';
import ClimbsListDrawer from './ClimbsListDrawer';
import {
  boardClimbsQuery,
  boardFiltersStorageKey,
  boardSearchStorageKey,
  normalizeBoardFilters,
  type BoardFilters,
} from './board-filters';

/** Larger pages than the tab's: a row here is one line, and the server caps it at 50. */
const PER_PAGE = 50;

/**
 * How far the list goes looking for the problem on screen before it gives up
 * and shows what it has: on a Moonboard's library that is 600 problems, more
 * than any list scrolled through to open one.
 */
const MAX_PAGES_TO_FIND_CURRENT = 12;

interface BoardClimbsListDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  board: Board;
  /** The problem on screen, highlighted and scrolled to. */
  currentSlug: string;
  /** A problem picked: the list as loaded and its place in it, for the swipe. */
  onPick: (climb: Climb, climbs: Climb[], index: number) => void;
}

/**
 * The board's problems from a problem's own page (see ClimbsListDrawer).
 *
 * Asked of the server rather than read from the list saved on the way in: a
 * board's library runs to hundreds of problems, and the tab only ever loaded
 * the pages it was scrolled through. It lists what the tab listed: the same filters (remembered per board) and the
 * same name search (left in the session when the problem was opened), asked of
 * the same endpoint, so the order is the server's and matches. Opened on the
 * problem on screen, highlighted: the pages are read until it turns up, then
 * the rest come in as the list is scrolled.
 */
export default function BoardClimbsListDrawer({
  open,
  onOpenChange,
  gymSlug,
  board,
  currentSlug,
  onPick,
}: BoardClimbsListDrawerProps) {
  const { t } = useTranslation();
  const [climbs, setClimbs] = useState<Climb[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(0);

  // Read when the drawer opens rather than once: the tab may have changed them
  // since this page was opened, and back on it they would be what it shows.
  const queryRef = useRef<BoardClimbsQuery>({});

  useEffect(() => {
    if (!open) return;

    const filters = normalizeBoardFilters(
      localStorageService.get<Partial<BoardFilters>>(boardFiltersStorageKey(board.id))
    );
    const name = sessionStorage.getItem(boardSearchStorageKey(board.id)) ?? '';
    queryRef.current = boardClimbsQuery(filters, name);

    let cancelled = false;
    setLoading(true);
    setClimbs([]);
    setHasMore(false);

    (async () => {
      const loaded: Climb[] = [];
      let page = 0;
      let more = true;
      try {
        while (more && page < MAX_PAGES_TO_FIND_CURRENT) {
          page += 1;
          const result = await devicesService.listBoardClimbs(gymSlug, board.id, {
            ...queryRef.current,
            page,
            per_page: PER_PAGE,
          });
          if (cancelled) return;
          loaded.push(...result.climbs);
          more = result.hasMore;
          if (result.climbs.some((climb) => climb.slug === currentSlug)) break;
        }
      } catch (error) {
        console.error('Failed to fetch board climbs:', error);
        more = false;
      }
      if (cancelled) return;
      pageRef.current = page;
      setClimbs(loaded);
      setHasMore(more);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, gymSlug, board.id, currentSlug]);

  const loadingMoreRef = useRef(false);
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const nextPage = pageRef.current + 1;
      const result = await devicesService.listBoardClimbs(gymSlug, board.id, {
        ...queryRef.current,
        page: nextPage,
        per_page: PER_PAGE,
      });
      pageRef.current = nextPage;
      setClimbs((prev) => [...prev, ...result.climbs]);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Failed to fetch more board climbs:', error);
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [gymSlug, board.id]);

  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  });

  // A callback ref: the sentinel comes and goes with `hasMore`, and inside a
  // drawer it only exists once the content has mounted.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current();
      },
      { threshold: 0.1 }
    );
    observerRef.current.observe(node);
  }, []);

  return (
    <ClimbsListDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('climbs.board.listTitle')}
      description={t('climbs.board.listDescription')}
      emptyText={t('climbs.board.noMatches')}
      climbs={climbs}
      loading={loading}
      currentSlug={currentSlug}
      onPick={onPick}
      footer={
        hasMore && (
          <div ref={sentinelRef} className="flex justify-center p-4">
            {loadingMore && <Loader2 className="size-5 animate-spin text-muted-foreground" />}
          </div>
        )
      }
    />
  );
}
